import React, { useEffect, useState } from 'react';
import * as DT from './index.styled';
import { TablePaginationConfig, Form, Select, Button } from 'antd';
import { TableRowSelection } from 'antd/es/table/interface';
import { useRecoilValue } from 'recoil';
import { userAtom } from '@/recoil/atoms/user.atom';
import axiosRequest from '@/api';
import { ResData } from '@/@types';
import { AgencyData } from '@/@types/Agency';
import { ReloadOutlined } from '@ant-design/icons';

const { Option } = Select;

interface DataTableProps<T> {
  data: T[];
  columns: any[];
  totalCount?: number;
  handleAddClick?: () => void;
  handleMobileListClick?: () => void;
  rowSelection?: TableRowSelection<T>;
  pagination?: TablePaginationConfig;
  onChange?: (pagination: TablePaginationConfig, filters?: any) => void;
  tableTitle?: string;
  scroll?: { x?: string, y?: string };
  loading?: boolean;
  onFilterChange?: (field: string, value: string | null) => void; // null을 허용하도록 수정
  showAgencyFilter?: boolean; // 대행사 필터 표시 여부
}

function DataTable<T>({
  data,
  columns,
  handleAddClick,
  handleMobileListClick,
  pagination,
  onChange,
  totalCount,
  tableTitle,
  scroll,
  loading,
  onFilterChange,
  showAgencyFilter = false, // 기본값은 false
}: DataTableProps<T>) {
  const user = useRecoilValue(userAtom);
  const [agencies, setAgencies] = useState<AgencyData[]>([]);
  const [filterForm] = Form.useForm();

  // 기본 페이지네이션 설정
  const defaultPaginationConfig = {
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '30', '40'],
    defaultPageSize: 20, // 기본 페이지당 행의 수
  };

  const finalPaginationConfig = {
    ...defaultPaginationConfig,
    total: totalCount,
    ...pagination,
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    onChange?.(newPagination);
  };

  // 대행사 목록 불러오기
  const fetchAgencies = async () => {
    if (showAgencyFilter) {
      try {
        const response = await axiosRequest.requestAxios<ResData<AgencyData[]>>('get', '/agencies');
        setAgencies(response.data);
      } catch (error) {
        console.error("fetchAgencies error: ", error);
      }
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, [showAgencyFilter]);

  // 대행사 선택 이벤트 핸들러
  const handleSelectChange = (value: string | number) => {
    if (onFilterChange) {
      onFilterChange('agency_id', String(value));
    }
  };

  // 필터 초기화 핸들러
  const handleResetFilter = () => {
    filterForm.resetFields();
    if (onFilterChange) {
      onFilterChange('agency_id', null); // null을 전달하여 필터 제거
    }
  };

  return (
    <DT.Wrapper>
      <DT.TableTopSection>
        <DT.TableTitle>{tableTitle}</DT.TableTitle>
        <DT.TableTopButtonSection>
          <div>
            {handleAddClick && (user?.role_name == 'admin' || user?.category != 'c') && (
              <DT.AddContainer>
                <DT.AddButton onClick={handleAddClick}>등록</DT.AddButton>
              </DT.AddContainer>
            )}
          </div>
          <div>
            {handleMobileListClick && (user?.role_name == 'admin' || user?.category != 'c') && (
              <DT.AddContainer>
                <DT.MobileListButton onClick={handleMobileListClick}>오퍼월 뷰</DT.MobileListButton>
              </DT.AddContainer>
            )}
          </div>
        </DT.TableTopButtonSection>
      </DT.TableTopSection>

      {/* 대행사 필터 영역 */}
      {showAgencyFilter && (
        <DT.FilterSection>
          <Form form={filterForm} layout="inline">
            <Form.Item name="agency_id" >
              <Select
                showSearch
                placeholder="대행사 선택 (검색 가능)"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option!.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleSelectChange}
                style={{ width: '200px' }}
                allowClear
              >
                {agencies.map(agency => (
                  <Option key={agency.id?.toString()} value={agency.id?.toString()}>
                    {agency.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                title="필터 초기화"
              >
                초기화
              </Button>
            </Form.Item>
          </Form>
        </DT.FilterSection>
      )}

      <DT.CustomStyledTable
        dataSource={data as any}
        columns={columns}
        pagination={finalPaginationConfig}
        onChange={handleTableChange}
        scroll={{ x: 'max-content', y: `calc(100vh - ${showAgencyFilter ? '110px' : '60px'})` }}
        loading={loading}
      />
    </DT.Wrapper>
  );
}

export default DataTable;